import { Button } from '@invent/wl-ui-kit-next/ui';
import { Download } from 'lucide-react';
import * as React from 'react';

import {
  ChoiceSelect,
  CondensedTip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useCondensed
} from '@/design';
import { SAVE_REFUSED, saveFile } from '@/domains/api';
import { secAdvApi } from '../../api/sec-adv-api';
import type { ISecAdvExportOptions, ISecAdvFirmQuery } from '../../api/sec-adv-contracts';
import { refusalMessage } from '../../refusal';

const FORMATS = [
  { value: 'xlsx', label: 'Excel (.xlsx)' },
  { value: 'csv', label: 'CSV' }
];

const HEADERS = [
  { value: 'labels', label: 'Readable labels — Business Name, Total AUM' },
  { value: 'sec', label: 'SEC monthly file headers — 5F(2)(c)' }
];

/**
 * "Export" (SR-423): the list as a file under its search, filters, sort and columns —
 * `GET /firms/export` — the visible page or the first 1,000 or 10,000 rows, with readable labels
 * or the SEC monthly files' headers. In a compact bar, its icon alone.
 */
export function ExportButton({
  query,
  fields,
  pageLabel
}: {
  /** The list's query, its `page` and `pageSize` the rows shown. */
  query: ISecAdvFirmQuery;
  fields: string[];
  /** What the choice of those rows says, their count in it: "This page (100 rows)". */
  pageLabel: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [format, setFormat] = React.useState<ISecAdvExportOptions['format']>('xlsx');
  const [rows, setRows] = React.useState('page');
  const [headers, setHeaders] = React.useState<ISecAdvExportOptions['headers']>('labels');
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const { condensed } = useCondensed();

  const download = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const answer = await secAdvApi.exportFirms(query, {
        format,
        headers,
        limit: rows === 'page' ? undefined : Number(rows),
        fields
      });
      const today = new Date().toISOString().slice(0, 10);
      if (!saveFile(answer.file, answer.fileName ?? `sec-adv-firms-${today}.${format}`)) {
        setSaid(SAVE_REFUSED);
        return;
      }
      setOpen(false);
    } catch (error) {
      setSaid(refusalMessage(error, 'The file could not be written.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <CondensedTip label="Export">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={condensed ? 'size-8 p-0' : 'h-8 gap-1.5 font-normal'}
          aria-label={condensed ? 'Export' : undefined}
          onClick={() => {
            setSaid(null);
            setOpen(true);
          }}
        >
          <Download className="size-4" aria-hidden />
          {!condensed && 'Export'}
        </Button>
      </CondensedTip>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export firms</DialogTitle>
          </DialogHeader>
          <DialogBody
            as="form"
            className="text-sm"
            onSubmit={(event) => {
              event.preventDefault();
              void download();
            }}
          >
            <Field
              label="Format"
              value={format}
              onChange={(value) => setFormat(value as ISecAdvExportOptions['format'])}
              choices={FORMATS}
            />
            <Field
              label="Rows"
              value={rows}
              onChange={setRows}
              choices={[
                { value: 'page', label: pageLabel },
                { value: '1000', label: 'The first 1,000 rows' },
                { value: '10000', label: 'The first 10,000 rows' }
              ]}
            />
            <Field
              label="Column headers"
              value={headers}
              onChange={(value) => setHeaders(value as ISecAdvExportOptions['headers'])}
              choices={HEADERS}
            />
            {said && (
              <p role="alert" className="text-destructive">
                {said}
              </p>
            )}
            <DialogFooter className="px-0 pb-0">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Writing the file…' : 'Download'}
              </Button>
            </DialogFooter>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** A choice under its label. */
function Field({
  label,
  value,
  onChange,
  choices
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  choices: { value: string; label: string }[];
}) {
  const id = React.useId();
  return (
    <div className="grid gap-1.5">
      <span id={id} className="font-medium">
        {label}
      </span>
      <ChoiceSelect labelledBy={id} value={value} onChange={onChange} choices={choices} />
    </div>
  );
}
